// Marketing Module Tour
// Email campaigns, SMS campaigns, and social media management

export const marketingTour = {
  id: 'marketing',
  name: 'Marketing Module Tour',
  displayName: 'Launch Powerful Marketing Campaigns',
  description: 'Send emails, SMS, and manage social media—all in one place',
  estimatedDuration: '6 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux', 'contacts'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Marketing
    {
      element: 'nav #sb_email-marketing',
      popover: {
        title: '📧 Welcome to Marketing',
        description: `
          Your complete marketing command center! Send email campaigns, SMS blasts, 
          schedule social media posts, and track everything—all from one place. 
          No more juggling multiple platforms. Let's explore!
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Marketing Sections Overview
    {
      popover: {
        title: '🗂️ Marketing Sections',
        description: `
          The Marketing module includes:
          • **Emails**: Create and send email campaigns
          • **Social Planner**: Schedule posts to Facebook, Instagram, LinkedIn
          • **Templates**: Save reusable email designs
          • **Analytics**: Track opens, clicks, and engagement
          
          Each section has powerful tools to help you reach your audience effectively.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Email Campaigns
    {
      element: '#tb_email-builder',
      popover: {
        title: '📨 Email Campaigns',
        description: `
          Send newsletters, promotions, or announcements to your contact list:
          • Design emails with drag-and-drop builder
          • Use pre-made templates or start from scratch
          • Segment your audience (send to specific tags or lists)
          • Schedule sends for optimal times
          • Track opens, clicks, and replies
        `,
        side: 'left',
        align: 'center',
        proTip: 'Always send a test email to yourself first to check formatting and links!'
      }
    },

    // Step 4: Email Builder
    {
      popover: {
        title: '🎨 Drag-and-Drop Email Builder',
        description: `
          Creating beautiful emails is easy with the visual builder:
          • **Drag elements**: Text, images, buttons, dividers
          • **Customize**: Colors, fonts, spacing to match your brand
          • **Merge fields**: Insert {{first_name}} for personalization
          • **Mobile preview**: See how it looks on phones
          • **Save as template**: Reuse successful designs
          
          No coding required! If you know PowerPoint, you can build emails.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 5: Email Templates
    {
      element: '[href*="template"], button:contains("Templates")',
      popover: {
        title: '📋 Email Templates',
        description: `
          Don't start from scratch every time! Save your best email designs as templates:
          • **Pre-built templates**: Choose from newsletter, promo, announcement styles
          • **Your templates**: Save custom designs for quick reuse
          • **Industry templates**: Pre-designed for different businesses
          
          Just pick a template, customize the content, and send. Done in minutes!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 6: Audience Selection
    {
      popover: {
        title: '🎯 Target Your Audience',
        description: `
          Who should receive your campaign? You have options:
          • **All contacts**: Everyone in your database
          • **Smart Lists**: Pre-filtered segments (Hot Leads, VIPs, etc.)
          • **Tags**: Anyone with specific tags
          • **Custom filter**: Build complex criteria (location + tag + behavior)
          
          Smart targeting = better engagement = more sales.
        `,
        side: 'over',
        align: 'center',
        proTip: 'Never send to everyone. Segment your audience for relevant, targeted messages that convert better!'
      }
    },

    // Step 7: Send Options
    {
      popover: {
        title: '📤 Sending Options',
        description: `
          Control how and when your campaign goes out:
          • **Send Now**: Immediately send to everyone
          • **Schedule**: Send at a specific date/time (great for time zones!)
          • **Batch Send**: Spread sends over hours/days to avoid spam filters
          • **A/B Test**: Send two versions to small groups, then best performer to rest
          
          Schedule campaigns in advance and let them run automatically.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 8: SMS Campaigns
    {
      element: '[href*="sms"], button:contains("SMS")',
      popover: {
        title: '💬 SMS Campaigns',
        description: `
          Text messaging gets 98% open rates! Send SMS campaigns for:
          • **Urgent announcements**: "Flash sale ends tonight!"
          • **Appointment reminders**: "See you tomorrow at 2pm"
          • **Event invitations**: "Join our webinar Thursday"
          • **Quick updates**: Short, time-sensitive messages
          
          Keep messages under 160 characters for best results.
        `,
        side: 'left',
        align: 'center',
        proTip: 'SMS is powerful but use sparingly. Too many texts = unsubscribes. Reserve SMS for important, timely messages!'
      }
    },

    // Step 9: Social Planner
    {
      element: '#tb_social-planner',
      popover: {
        title: '📱 Social Media Planner',
        description: `
          Schedule posts to multiple social platforms:
          • **Facebook**: Posts and Stories
          • **Instagram**: Feed posts and Stories
          • **LinkedIn**: Professional updates
          • **Google Business Profile**: Local updates
          
          Create posts once, schedule to multiple platforms, and maintain 
          consistent social presence without logging into each platform daily.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 10: Social Calendar
    {
      popover: {
        title: '🗓️ Content Calendar',
        description: `
          The Social Planner includes a calendar view showing all scheduled posts:
          • **Plan ahead**: Schedule weeks or months in advance
          • **Visual planning**: See gaps in your posting schedule
          • **Drag to reschedule**: Move posts to different dates easily
          • **Bulk scheduling**: Upload multiple posts at once
          
          Batch-create content when inspired, schedule it, and stay consistent effortlessly.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 11: Campaign Analytics
    {
      element: '[href*="analytic"], [href*="report"], button:contains("Analytics")',
      popover: {
        title: '📊 Track Campaign Performance',
        description: `
          Every campaign provides detailed analytics:
          • **Open rate**: % who opened your email
          • **Click rate**: % who clicked links
          • **Bounce rate**: Invalid email addresses
          • **Unsubscribes**: Who opted out
          • **Link clicks**: Which links got most clicks
          
          Use this data to improve future campaigns. A/B test subject lines to boost opens!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 12: Compliance & Best Practices
    {
      popover: {
        title: '✅ Stay Compliant',
        description: `
          Follow these rules to avoid spam filters and legal issues:
          • **Include unsubscribe link**: Required by law
          • **Add physical address**: Your business address in footer
          • **Honor opt-outs**: Remove unsubscribes immediately
          • **Don't buy lists**: Only email people who opted in
          • **Warm up new domains**: Start small, gradually increase volume
          
          The system helps with most of this automatically!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 13: Marketing Automation Integration
    {
      popover: {
        title: '⚡ Connect with Automation',
        description: `
          Marketing works even better with automation:
          • **Trigger campaigns**: Send email when tag is added
          • **Drip sequences**: Multi-email series over days/weeks
          • **Re-engagement**: Auto-email contacts who haven't opened in 30 days
          • **Abandoned cart**: Follow up when someone doesn't complete purchase
          
          Go to the Automation module to set up marketing workflows!
        `,
        side: 'over',
        align: 'center',
        proTip: 'Combine Marketing + Automation for powerful nurture sequences that convert leads into customers automatically!'
      }
    },

    // Step 14: Completion
    {
      popover: {
        title: '🎉 You\'re a Marketing Master!',
        description: `
          Fantastic! You now know how to:
          ✅ Create and send email campaigns
          ✅ Design beautiful emails with templates
          ✅ Send targeted SMS messages
          ✅ Schedule social media posts
          ✅ Track campaign performance
          ✅ Stay compliant with email laws
          
          **Next Steps:**
          • Create your first email campaign (start with a template!)
          • Set up your email signature in Settings
          • Schedule 1 week of social posts to get started
          • Explore Automation tour for drip campaigns
          
          Your marketing is about to level up! 🚀
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Start Marketing! →'
      }
    }
  ]
};
